/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import PropTypes from 'prop-types';
import { APP_PAGES, PLATFORMS } from '../../config/const.config';
import logoImg from "../../assets/images/hbcu_go/logo.png";
import scrollAppView from '../../utils/viewScroll.util';
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";

const TopMenu = ({
  menuData,
  activePage,
  handlePageChange,
  activePageLayoutType,
  isAnyLiveEvent,
  hideMenus = false,
  hideSearch = false,
  showLogoOnly = false,
}) => {
  const sidebarRef = useRef(null);
  const { videoTitle } = useParams();
  const navigate = useNavigate();
  const appName = process.env.REACT_APP_NAME;
  const location = useLocation();
  const menuDataLength = menuData === undefined ? 0 : menuData.length;
  const isDetailPage = location.pathname.match(/^\/movies\/\d+\/\d+\/[^/]+$/);
  const isSeriesDetailPage = location.pathname.match(/^\/series\/\d+\/\d+\/[^/]+$/);
  const previousPathRef = useRef(null);
  const [showSideMenu, setshowSideMenu] = useState(false);
  // platform web state variable
  const [colorChange, setColorchange] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 1400);
  const currentPath = decodeURIComponent(location.pathname.split("/")[1] || "");

  const [query, setQuery] = useState('');
  const timeoutIdRef = useRef(null);
  const seriesId = window.location.pathname.split('/')[4];
  const desktopInputRef = useRef(null); // Separate ref for desktop
  const mobileInputRef = useRef(null);  // Separate ref for mobile

  // helper: detect if url is a search url (no hardcoded path)
  const isSearchLocation = useCallback((loc) => loc.search && loc.search.includes("search="), []);

  // Sync query from URL on search change (stable)
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const searchQuery = queryParams.get('search');
    if (searchQuery !== null) { // Only set if explicitly present (avoids empty string issues)
      setQuery(searchQuery);
    }
  }, [location.search]);

  // Refined focus: Only on location.search change (navigation to search), not query.
  // Checks if already focused to avoid interrupting user selection/typing.
  // Uses rAF for smoothness + explicit cursor positioning to ensure blinking cursor appears.
  useEffect(() => {
    if (!isSearchLocation(location)) return; // Only focus on search navigation

    const focusInput = () => {
      // Determine which input to focus based on visibility/mobile
      const targetInput = showSearch ? mobileInputRef.current : desktopInputRef.current;
      if (targetInput && targetInput !== document.activeElement) {
        targetInput.focus();
        // Position cursor at end of text to ensure visible blinking cursor
        const len = targetInput.value.length;
        targetInput.setSelectionRange(len, len);
        // Optional: Scroll input into view if hidden/off-screen
        targetInput.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    };

    // Zero-delay timeout + rAF for post-render, but immediate enough for cursor
    setTimeout(() => requestAnimationFrame(focusInput), 0);
  }, [location.search, showSearch, isSearchLocation]); // No 'query' dep to avoid typing refocus


const capturePreviousPathIfNeeded = () => {
  // If there's already a captured previous path, don't overwrite
  if (previousPathRef.current) return;

  // If location.state.from exists (we came here via navigate(..., state: { from }))
  if (location.state && location.state.from) {
    previousPathRef.current = location.state.from;
    return;
  }

  // Otherwise capture current location as fallback
  previousPathRef.current = location.pathname + location.search;
};

const handleSearchChange = useCallback((event) => {
  const newQuery = event.target.value;
  setQuery(newQuery);

  if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);

  // If user cleared the input — preserve previousPathRef, then navigate back
  if (newQuery.length === 0) {
    timeoutIdRef.current = setTimeout(() => {
      if (newQuery.trim().length === 0) {
        // prefer explicit state.from if present, else use captured previous path
        const fromState = location.state && location.state.from;
        const target = fromState || previousPathRef.current || '/';

        // If target looks like /search or same as current, fallback to root
        if (target === '/search' || target === location.pathname + location.search) {
          navigate('/', { replace: true });
        } else {
          // normal navigate back
          navigate(target, { replace: true });
        }

        previousPathRef.current = null; // reset after using it
      }
    }, 500);
    return;
  }

  if (newQuery.length < 3) return;

  // BEFORE starting the navigate debounce, capture previous path (only once)
  capturePreviousPathIfNeeded();

  // debounce navigate to /search
  timeoutIdRef.current = setTimeout(() => {
    // Use navigate (always) so react-router updates location
    navigate(`/search?search=${encodeURIComponent(newQuery)}`, {
      replace: true,
      state: { from: previousPathRef.current },
    });
  }, 1500);
}, [location.pathname, location.search, location.state, navigate]);

  // Optional: Preserve selection during potential re-renders (advanced, if still needed)
  const [selectionRange, setSelectionRange] = useState(null);
  const handleSelect = useCallback((e) => {
    const input = e.target;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    if (start !== end) {
      setSelectionRange({ start, end });
    }
  }, []);
  useEffect(() => {
    if (selectionRange) {
      const targetInput = showSearch ? mobileInputRef.current : desktopInputRef.current;
      if (targetInput && document.activeElement === targetInput) {
        targetInput.setSelectionRange(selectionRange.start, selectionRange.end);
      }
    }
  }, [query, selectionRange, showSearch]); // Restore on query re-render

  const handleScroll = () => {
    const focusedElements = window.document.querySelectorAll(
      '.prj-element.focused'
    );
    if (focusedElements.length > 0) {
      scrollAppView(focusedElements[0]);
      if (
        activePageLayoutType !== 'grid' &&
        activePage !== 'search'
      ) {
        if (focusedElements[0].id === 'top-menu-0') {
          if (window.document.querySelector('.live-video-container')) {
            if (window.document.querySelectorAll('.page-container')) {
              window.document.querySelectorAll(
                '.page-container'
              )[0].style.marginTop = `788px`;
            }
          }
        }
      }
    }
  };

  const openScreen = () => {
    if (PLATFORMS.WEB) {
      navigate('/');
    }
  };

  const changeNavbarColor = () => {
    if (PLATFORMS.WEB) {
      if (window.scrollY >= 5) {
        setColorchange(true);
      } else {
        setColorchange(false);
      }
    }
  };
  if (PLATFORMS.WEB) {
    window.addEventListener('scroll', changeNavbarColor);
  }

  const mobMenu = () => {
    setshowSideMenu(true);
    const bodyEl = document.body;
    if (bodyEl) {
      bodyEl.classList.add("menu-open");
    }
  };
  const closeNavBar = () => {
    setshowSideMenu(false);
    const bodyEl = document.body;
    if (bodyEl) {
      bodyEl.classList.remove("menu-open");
    }
  };
  const toggleSearch = () => {
    setShowSearch(!showSearch);
    // Focus mobile input when toggled on (post-render)
    if (!showSearch) {
      setTimeout(() => {
        const focusInput = () => {
          if (mobileInputRef.current && mobileInputRef.current !== document.activeElement) {
            mobileInputRef.current.focus();
            // Ensure cursor at end
            const len = mobileInputRef.current.value.length;
            mobileInputRef.current.setSelectionRange(len, len);
          }
        };
        requestAnimationFrame(focusInput);
      }, 0);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        showSideMenu
      ) {
        setshowSideMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSideMenu]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 1400);
      setshowSideMenu(false);
    };
  
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Helper: Check if current location is search page
  const isSearchPage = location.pathname === '/search';

  return (
    <>
      {(menuDataLength > 0 || showLogoOnly) && (
        <nav
          className={`nav top-navigation navbar ${
            colorChange ? "colorChange" : ""
          } ${(isDetailPage || isSeriesDetailPage) ? "detailPage_navbar" : ""}
          ${isAnyLiveEvent ? "live-navbar" : ""}`}
          id="top-navigation"
        >
          <button
            type="button"
            className="hide"
            id="top-bar-tabs"
            onClick={handleScroll}
          >
            Scroll
          </button>
          <div className="nav-left menu-links align-items-center justify-content-center justify-content-md-between">            
              <div className="col-lg-2 col-md-3" style={{display: 'flex', alignItems: 'center', gap: '20px'}}>
                {!showLogoOnly && <div className="flex-box">
                <button
                  type="submit"
                  onClick={mobMenu}
                  className="my-button"
                  id="my-button"
                  name="my-button"
                  aria-label="Open menu"
                >
                  <svg viewBox="0 0 100 80" width="30" style={{ fill: 'white', position: 'relative', top: '2px' }}>
                    <rect width="80" height="8" />
                    <rect y="30" width="40" height="8" />
                    <rect y="60" width="80" height="8" />
                  </svg>
                </button>
              </div>}
              <div onClick={() => openScreen()}>
                <Link to='/' className="link brand-logo" onClick={() => menuData[0] && handlePageChange(menuData[0].id)} alt={appName || "HBCU GO"}>
                <img
                  className={`${colorChange ? 'brand-logo-scroll' : ''} ${activePage !== APP_PAGES.LANDINGPAGE ? 'brand-logo-home' : ''
                    }`}
                  src={logoImg}
                  alt={appName || "HBCU GO"}
                  name={appName}
                />
              </Link>
              </div>
            </div>

          {!hideMenus && !showLogoOnly && <div className="tabs-desktop col-12 col-md-auto">
            <div className={`menu-items ${!isMobileView && (videoTitle || seriesId) ? "menu-items-hidden-lg-screen" : ""}`}>
              {menuData.map((item, idx) => (
                <Link
                  to={`/${encodeURIComponent(item.title)}`}
                  id={`top-menu-${idx}`}
                  className={`link menu-item prj-element
                    ${!isSearchPage && (currentPath === item.title || Number(activePage) === Number(item.id)) ? " active focused" : ""}
                    ${colorChange || Number(activePage) !== Number(APP_PAGES.LANDINGPAGE) ? " scroll-top-menu" : ""}
                  `}
                  key={`menu-${item.id}`}
                  onClick={() => handlePageChange(item.id)}
                  data-focus-left={idx === 0 ? false : `#top-menu-${idx - 1}`}
                  data-focus-right={
                    idx + 1 === menuDataLength
                      ? '#top-menu-search'
                      : `#top-menu-${idx + 1}`
                  }
                  data-focus-up={false}
                  data-focus-down=".page-container .prj-element"
                >
                  {item.title}
                </Link>
              ))}
            </div>
          </div>}

          {!hideMenus && !showLogoOnly && <div className={showSideMenu ? 'tabs show-top-menubar' : 'tabs col-12 col-md-auto'}
            role="none"
            id="top-bar-tabs"
          >
            <div className="nav-close-btn-div">
              <button
                className="nav-close-btn"
                type="submit"
                title="Close Navigation"
                onClick={() => closeNavBar()}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="default-ltr-cache-v1ob21 e164gv2o4"
                  data-name="Plus"
                  alt=""
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M11 11V2H13V11H22V13H13V22H11V13H2V11H11Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
            <div className={`menu-items ${!isMobileView && (videoTitle || seriesId) ? "menu-items-hidden-lg-screen" : ""}`}>
              {menuData.map((item, idx) => (
                <Link
                  to={`/${encodeURIComponent(item.title)}`}
                  id={`top-menu-${idx}`}
                  className={`link menu-item prj-element
                    ${!isSearchPage && (currentPath === item.title || Number(activePage) === Number(item.id)) ? " active focused" : ""}
                    ${colorChange || Number(activePage) !== Number(APP_PAGES.LANDINGPAGE) ? " scroll-top-menu" : ""}
                  `}
                  key={`menu-${item.id}`}
                  onClick={() => handlePageChange(item.id)}
                  data-focus-left={idx === 0 ? false : `#top-menu-${idx - 1}`}
                  data-focus-right={
                    idx + 1 === menuDataLength
                      ? '#top-menu-search'
                      : `#top-menu-${idx + 1}`
                  }
                  data-focus-up={false}
                  data-focus-down=".page-container .prj-element"
                >
                  {item.title}
                </Link>
              ))}
            </div>
          </div>}

            {!hideSearch && !showLogoOnly && <div className="nav-right col-lg-2 col-md-4">
              <div className="mobile-search" onClick={toggleSearch}>
                <MagnifyingGlassIcon className="size-6 text-blue-500 cursor-pointer" />
              </div>
              <div className="search-box">
                <MagnifyingGlassIcon className="size-6 text-blue-500" />
                <input
                //  type="search" // Native handling for cursor visibility
                  placeholder="Search"
                  value={query}
                  onChange={handleSearchChange}
                  onSelect={handleSelect} // Preserve selection
                  ref={desktopInputRef}
                  key="desktop-search-input" // Stable key
                  autoFocus={false} // Avoid initial flash
                  // Ensure cursor is visible via inline style (override any CSS hiding caret)
                  style={{ caretColor: 'white' }} // Or your theme color; ensures blinking is visible
                />
              </div>
            </div>}
          </div>
          {!hideSearch && !showLogoOnly && <div className={`search-mobile ${showSearch ? "searchShow" : ""}`}>
            <div className="input-position">
              <MagnifyingGlassIcon className="searchIcon" />
              <input
                // type="search" // Consistent type
                placeholder="Search"
                value={query}
                onChange={handleSearchChange}
                onSelect={handleSelect} // Preserve selection
                ref={mobileInputRef}
                key="mobile-search-input" // Stable key
                autoFocus={showSearch} // Auto-focus when shown
                // Same caret style for consistency
                style={{ caretColor: 'white' }}
              />
            </div>
          </div>}
        </nav>

      )}
    </>

  );
};

TopMenu.propTypes = {
  menuData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
    })
  ).isRequired,
  handlePageChange: PropTypes.func.isRequired,
  activePageLayoutType: PropTypes.string,
  hideMenus: PropTypes.bool,
  hideSearch: PropTypes.bool,
  showLogoOnly: PropTypes.bool,
};

export default TopMenu;